import { AnimatePresence, motion } from "framer-motion";
import { Button } from "./ui/button";
import { FaMap, FaSearch } from "react-icons/fa";
import { cn } from "@/lib/utils";
import BrandLogo from "@/assets/cc-loading.gif";
import useMapStore from "@/store/mapStore";
import { IoCloseSharp } from "react-icons/io5";
import { FaSignsPost } from "react-icons/fa6";
import { useCallback, useEffect, useState } from "react";
import { t } from "i18next";
import { TbCurrentLocation } from "react-icons/tb";
import { Skeleton } from "./ui/skeleton";
import { PiArrowDownLight, PiArrowUpLight } from "react-icons/pi";
import PoiDetailsDistance from "./PoiDetailsDistance";
import Proximiio from "proximiio-js-library";
import Feature from "proximiio-js-library/lib/models/feature";
import { KioskModel } from "proximiio-js-library/lib/models/kiosk";
import { FloorModel } from "proximiio-js-library/lib/models/floor";

function WelcomeWindow({ closeHandler }: { closeHandler: () => void }) {
  const authSuccess = useMapStore((state) => state.authSuccess);
  const currentLang = useMapStore((state) => state.currentLang);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const haveRoutePreview = useMapStore((state) => state.haveRoutePreview);
  const routeDetails = useMapStore((state) => state.routeDetails);
  const kioskScaling = useMapStore((state) => state.kioskScaling);
  const [state, setState] = useState<
    "basic" | "start" | "kiosk" | "destination" | "route"
  >("basic");
  const [start, setStart] = useState<Feature | undefined>(undefined);
  const [destination, setDestination] = useState<Feature | undefined>(
    undefined,
  );
  const [kiosk, setKiosk] = useState<KioskModel | undefined>(undefined);
  const [floor, setFloor] = useState<FloorModel | undefined>(undefined);
  const [kioskFloor, setKioskFloor] = useState<FloorModel | undefined>(
    undefined,
  );

  const getFloorTitle = (type: string) => {
    const f = type === "feature" ? floor : kioskFloor;
    if (f && f.metadata && f.metadata[`title_${currentLang}`]) {
      return f.metadata[`title_${currentLang}`];
    }
    return f?.name;
  };

  const getFloor = useCallback(async (floorId: string, type: string) => {
    let floor;
    if (
      import.meta.env.MODE === "development" ||
      !import.meta.env.VITE_PROXIMIIO_BUNDLE_URL
    ) {
      floor = await Proximiio.Floors.getFloorById(floorId);
    } else if (import.meta.env.VITE_PROXIMIIO_BUNDLE_URL) {
      floor = await Proximiio.Floors.getFloorByIdBundle({
        bundleUrl: import.meta.env.VITE_PROXIMIIO_BUNDLE_URL,
        floorId,
      });
    }
    if (floor) {
      if (type === "feature") {
        setFloor(floor);
      } else if (type === "kiosk") {
        setKioskFloor(floor);
      }
    }
  }, []);

  const getFeature = useCallback(
    async (featureId: string, type: string) => {
      let feature;
      if (
        import.meta.env.MODE === "development" ||
        !import.meta.env.VITE_PROXIMIIO_BUNDLE_URL
      ) {
        feature = await Proximiio.Geo.getFeatureById(featureId);
      } else if (import.meta.env.VITE_PROXIMIIO_BUNDLE_URL) {
        feature = await Proximiio.Geo.getFeatureByIdBundle({
          bundleUrl: import.meta.env.VITE_PROXIMIIO_BUNDLE_URL,
          featureId,
        });
      }
      if (feature) {
        if (feature.properties.floor_id) {
          getFloor(feature.properties.floor_id, "feature");
        }
        if (type === "start") {
          setStart(feature);
        }
        if (type === "destination") {
          setDestination(feature);
        }
      }
    },
    [getFloor],
  );

  const getKiosk = useCallback(
    async (kioskId: string) => {
      let kiosk;
      if (
        import.meta.env.MODE === "development" ||
        !import.meta.env.VITE_PROXIMIIO_BUNDLE_URL
      ) {
        const kiosks = await Proximiio.Kiosks.getKiosks();
        kiosk = kiosks.data.find(
          (k) =>
            k.id === kioskId || k.name.toLowerCase() === kioskId.toLowerCase(),
        );
      } else if (import.meta.env.VITE_PROXIMIIO_BUNDLE_URL) {
        kiosk = await Proximiio.Kiosks.getKioskByIdBundle({
          bundleUrl: import.meta.env.VITE_PROXIMIIO_BUNDLE_URL,
          kioskId,
        });
      }
      if (kiosk) {
        if (kiosk.floor_id) {
          if (kiosk.floor_id) {
            getFloor(kiosk.floor_id, "kiosk");
          }
        }
        setKiosk(kiosk);
      }
    },
    [getFloor],
  );

  useEffect(() => {
    // Extract URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const startParam = urlParams.get("startFeature");
    const destinationParam = urlParams.get("remote_id");
    const kioskParam = urlParams.get("kiosk");

    if (authSuccess) {
      if (kioskParam) {
        setState("kiosk");
        getKiosk(kioskParam);
      }

      if (startParam) {
        setState("start");
        getFeature(startParam, "start");
      }

      if (destinationParam) {
        setState("destination");
        getFeature(destinationParam, "destination");
      }

      if ((startParam || kioskParam) && destinationParam) {
        setState("route");
      }
    }
  }, [getFeature, getKiosk, authSuccess]);

  const basicContent = () => {
    return (
      <>
        <h1 className="mb-5 text-center text-2xl font-bold">
          {t("welcome-heading")}
        </h1>
        <p className="container mb-8 max-w-sm text-center">{t("welcome-p")}</p>
        <h3 className="mb-2 w-full text-xl font-bold">{t("welcome-how-to")}</h3>
        <ul className="mb-4 flex w-full flex-1 flex-col gap-1 pl-8">
          <li>
            <FaSearch className="-ml-8 mr-3 inline text-lg" />
            {t("welcome-point-1")}
          </li>
          <li>
            <FaSignsPost className="-ml-8 mr-3 inline text-lg" />
            {t("welcome-point-2")}
          </li>
          <li>
            <FaMap className="-ml-8 mr-3 inline text-lg" />
            {t("welcome-point-3")}
          </li>
        </ul>
      </>
    );
  };

  const startTitles = () => {
    return (
      <div>
        <h1 className="text-center text-2xl">{t("you-are-on")}</h1>
        {start || kiosk?.floor_id ? (
          <>
            <h1 className="mb-10 text-center text-2xl font-bold">
              {t("floor")}:{" "}
              {kiosk?.floor_id
                ? getFloorTitle("kiosk")
                : getFloorTitle("feature")}
            </h1>
          </>
        ) : (
          <div className="mb-6 mt-2 flex items-center space-x-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-[150px]" />
              <Skeleton className="h-4 w-[100px]" />
            </div>
          </div>
        )}
      </div>
    );
  };

  const destinationTitles = () => {
    return (
      <div>
        <h1 className="text-center text-2xl">{t("you-are-going-to")}</h1>
        {destination ? (
          <>
            <h1 className="text-center text-2xl font-bold">
              {destination?.properties?.title}
            </h1>
            <h1 className="mb-10 text-center text-2xl font-bold">
              {t("floor")}: {getFloorTitle("feature")}
            </h1>
          </>
        ) : (
          <div className="mb-6 mt-2 flex flex-1 items-center space-x-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-[150px]" />
              <Skeleton className="h-4 w-[100px]" />
            </div>
          </div>
        )}
      </div>
    );
  };

  const startContent = () => {
    return (
      <>
        {startTitles()}

        <div className="container flex max-w-xs flex-1 gap-3">
          <TbCurrentLocation className="text-5xl" />
          <p>{t("start-location-note")}</p>
        </div>
      </>
    );
  };

  const destinationContent = () => {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        {destinationTitles()}
      </div>
    );
  };

  const routeContent = () => {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        {destinationTitles()}
        <div>
          {haveRouteDetails || haveRoutePreview ? (
            <ul
              className={cn(
                "my-5 flex flex-col gap-2 text-sm lg:gap-1 lg:text-base",
                haveRouteDetails && "mt-0",
                haveRouteDetails && mobileMode && "my-1.5",
              )}
            >
              {(haveRouteDetails || haveRoutePreview) &&
                routeDetails.start?.properties?.level !==
                  routeDetails.end?.properties?.level && (
                  <li className="flex items-center gap-2">
                    {routeDetails.start?.properties?.level <
                    routeDetails.end?.properties?.level ? (
                      <PiArrowUpLight className="text-lg text-primary" />
                    ) : (
                      <PiArrowDownLight className="text-lg text-primary" />
                    )}
                    <span>
                      {routeDetails.start?.properties?.level <
                      routeDetails.end?.properties?.level
                        ? t("ascend")
                        : t("descend")}{" "}
                      <strong>
                        {Math.abs(
                          routeDetails.start?.properties?.level -
                            routeDetails.end?.properties?.level,
                        )}{" "}
                        {Math.abs(
                          routeDetails.start?.properties?.level -
                            routeDetails.end?.properties?.level,
                        ) > 1
                          ? t("floors")
                          : t("floor").toLowerCase()}{" "}
                        {`${t("to")} ${getFloorTitle("feature")}`}{" "}
                        {t("floor").toLowerCase()}
                      </strong>
                    </span>
                  </li>
                )}
              {(haveRouteDetails || haveRoutePreview) && (
                <li className="flex items-center gap-2">
                  <PoiDetailsDistance />
                </li>
              )}
            </ul>
          ) : (
            <div className="mb-6 mt-2 flex items-center space-x-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-[150px]" />
                <Skeleton className="h-4 w-[100px]" />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      <div
        className={cn(
          "fixed z-100 flex h-full w-full items-center justify-center bg-black/50",
          mobileMode && "items-start",
          "z-[102]",
          !kioskScaling && "kiosk:origin-top-right kiosk:scale-150",
        )}
        style={{
          //@ts-expect-error color
          "--custom-handle-bg": `var(--primary)`,
        }}
      >
        <motion.div
          key="overlay"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          transition={{ duration: 0.2 }}
          className="relative h-full w-full rounded-b-none bg-white p-5 pt-16 lg:container lg:h-auto lg:max-w-lg lg:rounded-3xl lg:rounded-b-3xl lg:p-16 lg:py-10 lg:shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]"
        >
          <Button
            onClick={closeHandler}
            size="icon"
            className={cn(
              "absolute top-5 z-100 h-10 w-10 rounded-xl group-data-[dir=ltr]:right-5 group-data-[dir=rtl]:left-5",
              mobileMode &&
                "top-5 h-8 w-8 rounded-lg text-lg group-data-[dir=ltr]:right-5 group-data-[dir=rtl]:left-5",
            )}
          >
            <IoCloseSharp />
          </Button>
          <div className="flex h-full flex-col items-center">
            <img src={BrandLogo} className="mx-auto mb-4 w-36 lg:mb-8" />

            {state === "basic" && basicContent()}
            {(state === "start" || state === "kiosk") && startContent()}
            {state === "destination" && destinationContent()}
            {state === "route" && routeContent()}

            <Button
              className="mb-10 rounded-full px-8 font-thin lg:mb-0 lg:mt-10"
              onClick={closeHandler}
            >
              {t("start-navigation")}
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default WelcomeWindow;
